// Standalone client for the PUBLIC community-board + feedback endpoints
// (/api/public/mobile/...). Kept separate from mobile-api.ts on purpose: these
// are guest-friendly (no JWT, identity = deviceId), and this file must not depend
// on the authed transport. When EXPO_PUBLIC_API_URL is unset (web preview /
// backend down) every call returns null/false and the ideas-store falls back to
// its local mock — so the app is fully usable offline and in the web demo.

import { API_URL, type ApiEnvelope } from '@/api/client';
import { getDeviceId } from '@/api/device-id';
import type { Idea, IdeaComment, IdeaKind, IdeaStatus } from '@/store/ideas-store';

export function boardApiAvailable(): boolean {
  return API_URL != null;
}

// Backend IdeaDto (createdAt = ISO string from an Instant). Board ids are numeric
// server-side; the app keeps ids as strings, so we stringify on the way in.
interface IdeaDto {
  id: number;
  kind: IdeaKind;
  title: string;
  description: string | null;
  status: IdeaStatus;
  votes: number;
  votedByMe: boolean;
  createdAt: string;
  comments?: CommentDto[];
}

interface CommentDto {
  id: number;
  authorName: string;
  text: string;
  createdAt: string;
}

function mapComment(c: CommentDto): IdeaComment {
  return { id: String(c.id), author: c.authorName, text: c.text, createdAt: c.createdAt };
}

function mapIdea(d: IdeaDto): Idea {
  return {
    id: String(d.id),
    kind: d.kind,
    title: d.title,
    description: d.description ?? '',
    status: d.status,
    votes: d.votes,
    votedByMe: d.votedByMe,
    createdAt: d.createdAt,
    comments: (d.comments ?? []).map(mapComment),
  };
}

async function getJson<T>(path: string): Promise<ApiEnvelope<T> | null> {
  if (!API_URL) return null;
  try {
    const response = await fetch(`${API_URL}${path}`);
    return (await response.json()) as ApiEnvelope<T>;
  } catch {
    return null;
  }
}

async function postJsonPublic<T>(path: string, body: unknown): Promise<ApiEnvelope<T> | null> {
  if (!API_URL) return null;
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return (await response.json()) as ApiEnvelope<T>;
  } catch {
    return null;
  }
}

export async function fetchIdeasApi(): Promise<Idea[] | null> {
  const device = await getDeviceId();
  const res = await getJson<IdeaDto[]>(`/api/public/mobile/ideas?deviceId=${encodeURIComponent(device)}`);
  if (!res?.status || !res.data) return null;
  return res.data.map(mapIdea);
}

export async function fetchIdeaDetailApi(id: string): Promise<Idea | null> {
  const device = await getDeviceId();
  const res = await getJson<IdeaDto>(`/api/public/mobile/ideas/${id}?deviceId=${encodeURIComponent(device)}`);
  if (!res?.status || !res.data) return null;
  return mapIdea(res.data);
}

export async function createIdeaApi(input: {
  kind: IdeaKind;
  title: string;
  description: string;
  authorName?: string;
}): Promise<Idea | null> {
  const device = await getDeviceId();
  const res = await postJsonPublic<IdeaDto>(`/api/public/mobile/ideas`, { ...input, deviceId: device });
  if (!res?.status || !res.data) return null;
  return mapIdea(res.data);
}

export async function voteIdeaApi(id: string): Promise<{ votes: number; votedByMe: boolean } | null> {
  const device = await getDeviceId();
  const res = await postJsonPublic<{ votes: number; votedByMe: boolean }>(
    `/api/public/mobile/ideas/${id}/vote`,
    { deviceId: device },
  );
  if (!res?.status || !res.data) return null;
  return res.data;
}

export async function commentIdeaApi(id: string, text: string, authorName: string): Promise<IdeaComment | null> {
  const device = await getDeviceId();
  const res = await postJsonPublic<CommentDto>(`/api/public/mobile/ideas/${id}/comments`, {
    deviceId: device,
    authorName,
    text,
  });
  if (!res?.status || !res.data) return null;
  return mapComment(res.data);
}

/** Fire-and-forget rating delivery. Returns true if the backend accepted it. */
export async function submitFeedbackApi(rating: number, comment: string | null, appVersion?: string): Promise<boolean> {
  const device = await getDeviceId();
  const res = await postJsonPublic<unknown>(`/api/public/mobile/feedback`, {
    deviceId: device,
    rating,
    comment,
    appVersion,
  });
  return res?.status === true;
}
